import { Box, Radio, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { BsPlusCircle } from "react-icons/bs";
import { SlMinus } from "react-icons/sl";

const JsonTreeViewer = ({ data, onLeafSelect }) => {
  const [selectedPath, setSelectedPath] = useState([]);
  const [selectedNodeKey, setSelectedNodeKey] = useState("");
  const [openNodes, setOpenNodes] = useState(new Set());

  const generateNodeKey = (node, parentKey = "") => {
    const label = node?.code?.trim() || node?.description?.trim() || "unnamed";
    return parentKey ? `${parentKey} > ${label}` : label;
  };

  const findPathToNode = (node, targetNode, parentKey = "", path = []) => {
    const currentKey = generateNodeKey(node, parentKey);
    if (node.code && targetNode.code && node.code === targetNode.code) {
      return [...path, currentKey];
    }
    if (!node.items) return null;
    for (const child of node.items) {
      const result = findPathToNode(child, targetNode, currentKey, [
        ...path,
        currentKey,
      ]);
      if (result) return result;
    }
    return null;
  };

  useEffect(() => {
    const firstNode = Array.isArray(data) ? data[0] : data;
    if (!firstNode) return;
    const key = generateNodeKey(firstNode);
    setSelectedPath([key]);
    setOpenNodes(new Set([key]));
  }, [data]);

  useEffect(() => {
    if (selectedPath.length === 0) return;
    setOpenNodes(new Set(selectedPath));
  }, [selectedPath]);

  const toggleNode = (key) => {
    setOpenNodes((prev) => {
      const newSet = new Set(prev);
      newSet.has(key) ? newSet.delete(key) : newSet.add(key);
      return newSet;
    });
  };

  const findSiblings = (node, data, parentKey = "") => {
    const result = [];

    const recurse = (current, currentParentKey = "") => {
      const currentKey = generateNodeKey(current, currentParentKey);
      if (current.items?.includes(node)) {
        current.items.forEach((child) => {
          const siblingKey = generateNodeKey(child, currentKey);
          result.push(siblingKey);
        });
      }
      current.items?.forEach((child) => recurse(child, currentKey));
    };

    if (Array.isArray(data)) {
      data.forEach((item) => recurse(item));
    } else {
      recurse(data);
    }

    return result;
  };

  const handleNodeClick = (node, parentKey = "") => {
    const nodeKey = generateNodeKey(node, parentKey);
    let newPath = [];

    const traverse = (dataArr) => {
      for (const item of dataArr) {
        const foundPath = findPathToNode(item, node);
        if (foundPath) {
          newPath = foundPath;
          return true;
        }
      }
      return false;
    };

    if (Array.isArray(data)) {
      traverse(data);
    } else {
      const path = findPathToNode(data, node);
      if (path) newPath = path;
    }

    setSelectedNodeKey(nodeKey);
    setSelectedPath(newPath);

    const hasChildren = node.items && node.items.length > 0;

    if (hasChildren) {
      setOpenNodes((prevOpen) => {
        const newSet = new Set(prevOpen);
        const siblings = findSiblings(node, data, parentKey);
        const isAlreadyOpen = newSet.has(nodeKey);

        if (isAlreadyOpen) {
          newSet.delete(nodeKey);
        } else {
          siblings.forEach((siblingKey) => newSet.delete(siblingKey));
          newSet.add(nodeKey);
        }

        return newSet;
      });
    }

    if (node.is_leaf) {
      onLeafSelect?.(node.code, node); // Pass node for product details
    }
  };

  const renderNode = (node, level = 0, parentKey = "", i) => {
    const nodeKey = generateNodeKey(node, parentKey);
    const hasChildren = Array.isArray(node?.items) && node.items?.length > 0;
    const isOpen = openNodes.has(nodeKey);
    const isChecked = selectedPath.includes(nodeKey);
    const isSelected = selectedNodeKey === nodeKey;
    const isLeaf = node.is_leaf;

    return (
      <Box key={nodeKey} sx={{ ml: level * 2, mt: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            p: 1,
            pl: 1.5,
            backgroundColor: isSelected && isLeaf ? "#e0f7fa" : "#f9f9f9",
            borderRadius: 1,
            cursor: "pointer",
            userSelect: "none",
          }}
          onClick={() => handleNodeClick(node, parentKey)}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {hasChildren && (
              <span
                style={{
                  fontSize: "1.3em",
                  color: "#00A9CE",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "transform 0.2s ease-in-out",
                  transform: isOpen ? "rotate(180deg)" : "rotate(90deg)",
                }}
              >
                {isOpen ? <SlMinus /> : <BsPlusCircle />}
              </span>
            )}

            {isLeaf && (
              <Radio
                checked={selectedNodeKey === nodeKey}
                onChange={(e) => {
                  e.stopPropagation();
                  handleNodeClick(node, parentKey);
                }}
                value={nodeKey}
                size="small"
                onClick={(e) => e.stopPropagation()}
              />
            )}
            <Box my={0.5}>
              <Typography variant="body2">
                <span>
                  <strong>{node.code ? `[ ${node.code} ] : ` : ""}</strong>
                  {node.description}
                  {`${!hasChildren ? "" : " ?"}`}
                </span>
              </Typography>
            </Box>
          </div>
        </Box>

        {hasChildren &&
          isOpen &&
          node.items.map((child, i) =>
            renderNode(child, level + 1, nodeKey, i)
          )}
      </Box>
    );
  };

  return (
    <Box>
      {Array.isArray(data)
        ? data.map((node, index) => renderNode(node, 0, "", index))
        : renderNode(data, 0, "", 999)}
    </Box>
  );
};

export default JsonTreeViewer;
